import { useState } from 'react';
import ImageWithSkeleton from '@/components/base/ImageWithSkeleton';
import { ROOM_LIST } from './FavoriteRoomsPanel';

interface RoomCompareModalProps {
  darkMode: boolean;
  initialRoomA: string;
  initialRoomB: string;
  selectedStyle: string;
  getRoomInteriorImage: (label: string, style: string) => string;
  onClose: () => void;
}

export default function RoomCompareModal({
  darkMode,
  initialRoomA,
  initialRoomB,
  selectedStyle,
  getRoomInteriorImage,
  onClose,
}: RoomCompareModalProps) {
  const [roomAId, setRoomAId] = useState(initialRoomA);
  const [roomBId, setRoomBId] = useState(initialRoomB);

  const cardBg = darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const text = darkMode ? 'text-white' : 'text-gray-900';
  const subtext = darkMode ? 'text-gray-400' : 'text-gray-500';

  const roomA = ROOM_LIST.find((r) => r.id === roomAId) ?? ROOM_LIST[0];
  const roomB = ROOM_LIST.find((r) => r.id === roomBId) ?? ROOM_LIST[1];

  const imgA = getRoomInteriorImage(roomA.label, selectedStyle);
  const imgB = getRoomInteriorImage(roomB.label, selectedStyle);

  return (
    <div className="fixed inset-0 bg-black/75 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className={`w-full max-w-5xl rounded-2xl overflow-hidden shadow-2xl border ${cardBg}`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center">
              <i className="ri-layout-column-line text-white text-lg"></i>
            </div>
            <div>
              <h3 className={`text-lg font-bold ${text}`}>Room Comparison</h3>
              <p className={`text-xs ${subtext}`}>Side-by-side interior renders · {selectedStyle || 'Modern'} style</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={`w-9 h-9 rounded-xl flex items-center justify-center cursor-pointer transition-colors ${darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-600'}`}
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Room selectors */}
        <div className={`flex items-center gap-4 px-6 py-3 border-b ${darkMode ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-gray-50'}`}>
          {/* Room A selector */}
          <div className="flex-1 flex items-center gap-2">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${darkMode ? 'bg-teal-600 text-white' : 'bg-teal-500 text-white'}`}>A</div>
            <select
              value={roomAId}
              onChange={(e) => setRoomAId(e.target.value)}
              className={`flex-1 text-sm font-semibold rounded-xl px-3 py-2 border cursor-pointer outline-none transition-colors ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white focus:border-teal-500' : 'bg-white border-gray-200 text-gray-900 focus:border-teal-400'
              }`}
            >
              {ROOM_LIST.map((r) => (
                <option key={r.id} value={r.id} disabled={r.id === roomBId}>{r.label}</option>
              ))}
            </select>
          </div>

          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
            <i className="ri-arrow-left-right-line"></i>
            vs
          </div>

          {/* Room B selector */}
          <div className="flex-1 flex items-center gap-2">
            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${darkMode ? 'bg-rose-600 text-white' : 'bg-rose-500 text-white'}`}>B</div>
            <select
              value={roomBId}
              onChange={(e) => setRoomBId(e.target.value)}
              className={`flex-1 text-sm font-semibold rounded-xl px-3 py-2 border cursor-pointer outline-none transition-colors ${
                darkMode ? 'bg-gray-700 border-gray-600 text-white focus:border-rose-500' : 'bg-white border-gray-200 text-gray-900 focus:border-rose-400'
              }`}
            >
              {ROOM_LIST.map((r) => (
                <option key={r.id} value={r.id} disabled={r.id === roomAId}>{r.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Side-by-side renders */}
        <div className="flex overflow-hidden" style={{ height: 420 }}>
          {/* Room A */}
          <div className="flex-1 relative flex flex-col">
            {/* Label bar */}
            <div className={`flex items-center gap-2 px-4 py-2 border-b border-r ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
              <div className="w-5 h-5 rounded-full bg-teal-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">A</div>
              <div className={`w-6 h-6 flex items-center justify-center rounded-lg flex-shrink-0 ${roomA.bg}`}>
                <i className={`${roomA.icon} text-xs ${roomA.color}`}></i>
              </div>
              <span className={`text-sm font-bold ${text}`}>{roomA.label}</span>
              <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-teal-900/40 text-teal-300' : 'bg-teal-100 text-teal-700'}`}>{selectedStyle || 'Modern'}</span>
            </div>
            {/* Image */}
            <div className="flex-1 relative overflow-hidden border-r border-gray-200 dark:border-gray-700">
              <ImageWithSkeleton
                src={imgA}
                alt={roomA.label}
                className="w-full h-full object-cover object-top"
                darkMode={darkMode}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none"></div>
              <div className="absolute bottom-3 left-3 px-2.5 py-1.5 bg-black/60 backdrop-blur-sm rounded-lg text-white text-xs font-semibold flex items-center gap-1.5">
                <i className={`${roomA.icon} ${roomA.color}`}></i>
                {roomA.label}
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className={`w-px flex-shrink-0 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}></div>

          {/* Room B */}
          <div className="flex-1 relative flex flex-col">
            {/* Label bar */}
            <div className={`flex items-center gap-2 px-4 py-2 border-b ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
              <div className="w-5 h-5 rounded-full bg-rose-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">B</div>
              <div className={`w-6 h-6 flex items-center justify-center rounded-lg flex-shrink-0 ${roomB.bg}`}>
                <i className={`${roomB.icon} text-xs ${roomB.color}`}></i>
              </div>
              <span className={`text-sm font-bold ${text}`}>{roomB.label}</span>
              <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${darkMode ? 'bg-rose-900/40 text-rose-300' : 'bg-rose-100 text-rose-700'}`}>{selectedStyle || 'Modern'}</span>
            </div>
            {/* Image */}
            <div className="flex-1 relative overflow-hidden">
              <ImageWithSkeleton
                src={imgB}
                alt={roomB.label}
                className="w-full h-full object-cover object-top"
                darkMode={darkMode}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none"></div>
              <div className="absolute bottom-3 left-3 px-2.5 py-1.5 bg-black/60 backdrop-blur-sm rounded-lg text-white text-xs font-semibold flex items-center gap-1.5">
                <i className={`${roomB.icon} ${roomB.color}`}></i>
                {roomB.label}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={`flex items-center justify-between px-6 py-3 border-t ${darkMode ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-gray-50'}`}>
          <p className={`text-xs ${subtext}`}>
            <i className="ri-information-line mr-1"></i>
            Change the dropdowns above to compare any two rooms
          </p>
          <div className="flex items-center gap-2">
            {/* Swap rooms */}
            <button
              onClick={() => { const tmp = roomAId; setRoomAId(roomBId); setRoomBId(tmp); }}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer flex items-center gap-1.5 transition-colors ${
                darkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <i className="ri-arrow-left-right-line"></i>Swap
            </button>
            <button
              onClick={onClose}
              className="px-4 py-1.5 rounded-lg text-xs font-bold bg-gradient-to-r from-teal-500 to-cyan-500 text-white hover:from-teal-600 hover:to-cyan-600 transition-all cursor-pointer whitespace-nowrap"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
